import { Box, Button, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PrivacyPolicy, PrivacyPolicyConsentStatusEnum } from "@/api/generated/api";
import { Logout } from "@/api/logout";
import { getPrivacyPolicyConsent, postPrivacyPolicyConsent } from "@/api/privacyPolicyConsent";
import { CustomButton } from "@/components/Button";
import { MarkdownTextViewer } from "@/components/MarkdownTextViewer";
import { StyledModal } from "@/components/StyledModal";
import { blockBackButton } from "@/utilities/backButtonGuard";
import { clearStorage } from "@/utilities/storage";
import { SessionState, UserState } from "@/view_models/atoms";

import theme from "./theme";

type ConsentPhase = "none" | "privacy_policy";

const FALLBACK_ERROR_MESSAGE = "ネットワークエラーです。しばらくたってからお試しください";

// プラポリ同意状態を取得する（初回チェックと stale 時の再構成で共用）
const fetchConsentStatuses = async () => getPrivacyPolicyConsent();

// 取得・送信・phase 遷移のロジックを集約するフック。UI はこの戻り値を表示するだけにする
const useConsentGate = () => {
  const user = useRecoilValue(UserState);
  const setSession = useSetRecoilState(SessionState);

  const [phase, setPhase] = React.useState<ConsentPhase>("none");
  const [policyDocument, setPolicyDocument] = React.useState<PrivacyPolicy | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  // チェック済みユーザーの id（ネイティブはリロードが起きないため、ログアウト→別ユーザーで
  // ログインした場合も再チェックが走るよう user.id 単位で管理する。GET 失敗時は null に戻す）
  const checkedUserIdRef = React.useRef<number | null>(null);

  // GET 結果を state・phase に反映する。取得失敗は何もせず false を返す
  const applyFetchedConsents = React.useCallback(
    (policyConsentResult: Awaited<ReturnType<typeof fetchConsentStatuses>>): boolean => {
      if (!policyConsentResult.success) return false;

      // 回答が必要 = is_agreed が true ではなく、かつ文書が同梱されているとき
      const policyDocument = policyConsentResult.is_agreed ? null : policyConsentResult.policy;

      setPolicyDocument(policyDocument);
      setPhase(policyDocument ? "privacy_policy" : "none");
      return true;
    },
    []
  );

  // モーダル表示中は Android のハードウェア戻るボタンで回避されないようブロックする
  React.useEffect(() => {
    if (phase === "none") return undefined;
    const release = blockBackButton();
    return release;
  }, [phase]);

  React.useEffect(() => {
    // user がロードされていれば、初期登録・診断の完了状態に関わらず必ずチェックする
    if (!user?.id) return;
    if (checkedUserIdRef.current === user.id) return;

    checkedUserIdRef.current = user.id;

    const checkConsentStatuses = async () => {
      const fetchedConsent = await fetchConsentStatuses();
      // 取得失敗時はモーダルを出さず、次の user 更新時に再チェックできるよう解除する
      if (!applyFetchedConsents(fetchedConsent)) checkedUserIdRef.current = null;
    };
    checkConsentStatuses();
  }, [user, applyFetchedConsents]);

  // 「上記の内容に同意する」押下で agreed を送信する
  const submitConsents = () => {
    if (!policyDocument) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    // API ラッパーは失敗時も reject せず success: false のオブジェクトを返す
    postPrivacyPolicyConsent(PrivacyPolicyConsentStatusEnum.Agreed, policyDocument.id).then(async (postResult) => {
      if (postResult.success) {
        setIsSubmitting(false);
        setPhase("none");
        return;
      }

      // 400 = 表示中の版が改訂された（stale）可能性 → GET を再実行してフローを再構成する。
      // 無言で本文が入れ替わると不具合と誤認されるため、再構成後に内容が更新された旨の文言を表示する
      if (postResult.status === 400) {
        const isReconstructed = applyFetchedConsents(await fetchConsentStatuses());
        setIsSubmitting(false);
        setErrorMessage(isReconstructed ? postResult.error : FALLBACK_ERROR_MESSAGE);
        return;
      }

      // その他の失敗はフローを閉じずエラー表示のみ（リトライ = 再送。upsert 冪等のため安全）。
      // 文言は API ラッパーの原因別メッセージ（401=再ログイン促し等）をそのまま表示する
      setIsSubmitting(false);
      setErrorMessage(postResult.error ?? FALLBACK_ERROR_MESSAGE);
    });
  };

  // 「同意しない（ログアウト）」押下でログアウトし、ログイン画面へ戻す。
  // プラポリに同意しない場合はサービスを継続利用できず、かつ拒否用の API も存在しない
  // （PrivacyPolicyConsentStatusEnum は agreed のみ）ため、拒否 = セッション終了として扱う
  const declineConsent = () => {
    setIsSubmitting(true);
    setErrorMessage(null);

    Logout().then((isSucceeded) => {
      // ログアウト失敗時はフローを閉じず、再試行できるようエラー表示のみ
      if (!isSucceeded) {
        setIsSubmitting(false);
        setErrorMessage(FALLBACK_ERROR_MESSAGE);
        return;
      }

      setSession({ logged_in: false });
      clearStorage();
      window.location.reload();
    });
  };

  return {
    phase,
    policyDocument,
    isSubmitting,
    errorMessage,
    submitConsents,
    declineConsent,
  };
};

const useStyles = makeStyles(() => ({
  messageContent: {
    padding: "24px 24px 0 24px",
    zIndex: 1300,
  },
  title: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  description: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  bodyContainer: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    maxHeight: "40vh",
    overflowY: "auto",
    marginBottom: theme.spacing(2),
  },
  errorText: {
    textAlign: "center",
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: "0 24px 24px 24px",
  },
  declineButton: {
    textTransform: "none",
    color: theme.palette.text.secondary,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

type PrivacyPolicyConsentModalProps = {
  policyBody: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onAgree: () => void;
  onDecline: () => void;
};

// プラポリ改訂への同意（「上記の内容に同意する」＋「同意しない（ログアウト）」・×等の閉じる手段なし）。
// 同意しない場合はサービスを継続利用できないため、終了（ログアウト）へ導く
export const PrivacyPolicyConsentModal: React.FC<PrivacyPolicyConsentModalProps> = ({
  policyBody,
  isSubmitting,
  errorMessage,
  onAgree,
  onDecline,
}) => {
  const classes = useStyles();
  return (
    <StyledModal>
      <Box className={classes.messageContent}>
        <Typography className={classes.title} variant="h3">
          プライバシーポリシーが
          <br />
          改定されました
        </Typography>
        <Typography className={classes.description}>
          引き続きサービスをご利用いただくには、プライバシーポリシーへの同意が必要です。
        </Typography>
        <Typography className={classes.sectionTitle} variant="h4">
          プライバシーポリシー
        </Typography>
        <Box className={classes.bodyContainer}>
          <MarkdownTextViewer>{policyBody}</MarkdownTextViewer>
        </Box>
        {errorMessage && (
          <Typography className={classes.errorText} role="alert">
            {errorMessage}
          </Typography>
        )}
      </Box>
      <Box className={classes.actions}>
        <CustomButton customVariant="filled" fullWidth disabled={isSubmitting} onClick={onAgree}>
          上記の内容に同意する
        </CustomButton>
        <Button className={classes.declineButton} fullWidth disabled={isSubmitting} onClick={onDecline}>
          同意しない（ログアウト）
        </Button>
      </Box>
    </StyledModal>
  );
};

/**
 * 【エントリーポイント】プライバシーポリシー改訂同意モーダルのゲート。
 * ログイン済みの全ユーザーに対して未回答の文書があればモーダルを表示する。
 * 取得・送信・phase 遷移は useConsentGate に集約し、ここでは phase に応じてモーダルを出すだけ
 */
export const ConsentModals: React.FC = () => {
  const { phase, policyDocument, isSubmitting, errorMessage, submitConsents, declineConsent } = useConsentGate();

  if (phase === "privacy_policy" && policyDocument) {
    return (
      <PrivacyPolicyConsentModal
        policyBody={policyDocument.body}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        onAgree={submitConsents}
        onDecline={declineConsent}
      />
    );
  }

  return null;
};
